import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { GET_OFFICES_QUERY } from 'modules/locations/graphql/queries';
import {
  INightlyCountSettingsJson,
  INightlyCountSettingsNotificationBucket,
  INightlyCountSettingsProduct,
  NIGHTLY_COUNTS_NOTIFICATIONS_ALL_KEY,
  NIGHTLY_COUNTS_SETTINGS_TAB,
  NightlyProductBloodType,
  NightlyProductClass,
  PRODUCT_CLASSES_WITH_BLOOD_TYPE,
} from 'modules/nightlyCount/types/settings';
import { GET_USER_LIST } from 'modules/user/graphql/queries/user';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SAVE_APP_SETTINGS_MUTATION } from '../../graphql/mutations';
import { GET_APP_SETTINGS_QUERY } from '../../graphql/queries';

const { Text } = Typography;
const { Panel } = Collapse;

interface IOfficeOpt {
  id: string;
  name: string;
  region?: { id: string; name: string } | null;
}

interface IUserOpt {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const generateId = () =>
  `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const DEFAULT_TEMPLATE = `<div>
<p>Nightly count for <strong>{{officeName}}</strong> on {{date}}</p>
<p>Submitted by {{userName}} (<{{userEmail}}>)</p>
{{productCountList}}
</div>`;

const NightlyInventoryCountSettings: FC = () => {
  const { t } = useTranslation('nightlyCount.NightlyInventoryCountSettings');

  const [products, setProducts] = useState<INightlyCountSettingsProduct[]>([]);
  const [officeUsers, setOfficeUsers] = useState<Record<string, string[]>>({});
  const [notifications, setNotifications] = useState<
    Record<string, INightlyCountSettingsNotificationBucket>
  >({});
  const [isProductModalOpen, setProductModalOpen] = useState(false);
  const [draftProduct, setDraftProduct] = useState<
    Partial<INightlyCountSettingsProduct>
  >({});
  const [isModified, setIsModified] = useState(false);

  const officesQuery = useQuery<{ getOffices: IOfficeOpt[] }>(
    GET_OFFICES_QUERY
  );
  const usersQuery = useQuery<{
    getUsersList: { data: IUserOpt[]; meta: { total: number } };
  }>(GET_USER_LIST, {
    variables: {
      input: { filter: { isArchived: false }, take: 500, skip: 0 },
    },
  });

  const settingsQuery = useQuery<{
    getAppSettings: { settingsJson: INightlyCountSettingsJson } | null;
  }>(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: NIGHTLY_COUNTS_SETTINGS_TAB } },
    onCompleted: (data) => {
      const saved = data?.getAppSettings?.settingsJson;
      if (saved) {
        if (saved.products) setProducts(saved.products);
        if (saved.officeUsers) setOfficeUsers(saved.officeUsers);
        if (saved.notifications) setNotifications(saved.notifications);
      }
    },
  });

  const [saveAppSettings, { loading: isSaving }] = useMutation(
    SAVE_APP_SETTINGS_MUTATION,
    {
      onCompleted: () => {
        message.success(t('settingsSaved'));
        setIsModified(false);
      },
      onError: (e) => message.error(`${t('saveFailed')}: ${e.message}`),
    }
  );

  const offices: IOfficeOpt[] = officesQuery.data?.getOffices || [];
  const users: IUserOpt[] = usersQuery.data?.getUsersList?.data || [];

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName} (${u.email})`,
      })),
    [users]
  );

  const officeNameById = useMemo(() => {
    const m: Record<string, string> = {};
    offices.forEach((o) => {
      m[o.id] = `${o.name}${o.region?.name ? ` — ${o.region.name}` : ''}`;
    });
    return m;
  }, [offices]);

  const markModified = () => setIsModified(true);

  const handleSave = async () => {
    const json: INightlyCountSettingsJson = {
      products,
      officeUsers,
      notifications,
    };
    await saveAppSettings({
      variables: {
        input: {
          settingsTab: NIGHTLY_COUNTS_SETTINGS_TAB,
          settingsJson: json,
        },
      },
    });
  };

  const openAddProduct = () => {
    setDraftProduct({});
    setProductModalOpen(true);
  };

  const confirmAddProduct = () => {
    const d = draftProduct;
    if (!d.productTitle || !d.woocommerceProductId || !d.productClass) {
      message.error(t('requiredField'));
      return;
    }
    if (
      PRODUCT_CLASSES_WITH_BLOOD_TYPE.includes(d.productClass) &&
      !d.productBloodType
    ) {
      message.error(t('requiredField'));
      return;
    }
    const productClass = d.productClass as NightlyProductClass;
    const product: INightlyCountSettingsProduct = {
      id: generateId(),
      productTitle: d.productTitle as string,
      woocommerceProductId: Number(d.woocommerceProductId),
      productClass,
      productBloodType: PRODUCT_CLASSES_WITH_BLOOD_TYPE.includes(productClass)
        ? (d.productBloodType as NightlyProductBloodType)
        : null,
    };
    setProducts((p) => [...p, product]);
    setProductModalOpen(false);
    markModified();
  };

  const removeProduct = (id: string) => {
    setProducts((p) => p.filter((x) => x.id !== id));
    markModified();
  };

  const productColumns: ColumnsType<INightlyCountSettingsProduct> = [
    {
      title: t('productTitle'),
      dataIndex: 'productTitle',
      key: 'productTitle',
    },
    {
      title: t('wcPid'),
      dataIndex: 'woocommerceProductId',
      key: 'woocommerceProductId',
    },
    {
      title: t('productClass'),
      dataIndex: 'productClass',
      key: 'productClass',
    },
    {
      title: t('productBloodType'),
      dataIndex: 'productBloodType',
      key: 'productBloodType',
      render: (v) => v || '',
    },
    {
      title: '',
      key: 'remove',
      render: (_, r) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeProduct(r.id)}
        />
      ),
    },
  ];

  const notificationKeys = useMemo(() => {
    const keys = [
      NIGHTLY_COUNTS_NOTIFICATIONS_ALL_KEY,
      ...offices.map((o) => o.id),
    ];
    return Array.from(new Set(keys));
  }, [offices]);

  const updateNotificationBucket = (
    key: string,
    patch: Partial<INightlyCountSettingsNotificationBucket>
  ) => {
    setNotifications((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch,
      },
    }));
    markModified();
  };

  if (settingsQuery.loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title={t('products')}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAddProduct}
          >
            {t('addProduct')}
          </Button>
        }
      >
        <Table<INightlyCountSettingsProduct>
          rowKey={(r) => r.id}
          columns={productColumns}
          dataSource={products}
          pagination={false}
          size="small"
        />
      </Card>

      <Card title={t('users')}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {offices.map((office) => (
            <Row key={office.id} gutter={16} align="middle">
              <Col xs={24} md={8}>
                <Text strong>{office.name}</Text>{' '}
                {office.region?.name && (
                  <Text type="secondary">({office.region.name})</Text>
                )}
              </Col>
              <Col xs={24} md={16}>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  value={officeUsers[office.id] || []}
                  options={userOptions}
                  onChange={(v) => {
                    setOfficeUsers((prev) => ({ ...prev, [office.id]: v }));
                    markModified();
                  }}
                  optionFilterProp="label"
                  placeholder="Select users"
                />
              </Col>
            </Row>
          ))}
        </Space>
      </Card>

      <Card title={t('notifications')}>
        <Collapse>
          {notificationKeys.map((key) => {
            const label =
              key === NIGHTLY_COUNTS_NOTIFICATIONS_ALL_KEY
                ? t('allOffices')
                : officeNameById[key] || key;
            const bucket = notifications[key] || {};
            const emailUserIds = bucket.email?.userIds || [];
            const textUserIds = bucket.text?.userIds || [];
            const teamsUserIds = bucket.teams?.userIds || [];
            const template = bucket.template ?? DEFAULT_TEMPLATE;
            return (
              <Panel header={label} key={key}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form layout="vertical">
                    <Form.Item label={t('emailRecipients')}>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        value={emailUserIds}
                        options={userOptions}
                        onChange={(v) =>
                          updateNotificationBucket(key, {
                            email: { userIds: v },
                          })
                        }
                        optionFilterProp="label"
                      />
                    </Form.Item>
                    <Form.Item label={t('textRecipients')}>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        value={textUserIds}
                        options={userOptions}
                        onChange={(v) =>
                          updateNotificationBucket(key, {
                            text: { userIds: v },
                          })
                        }
                        optionFilterProp="label"
                        disabled
                      />
                    </Form.Item>
                    <Form.Item label={t('teamsRecipients')}>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        value={teamsUserIds}
                        options={userOptions}
                        onChange={(v) =>
                          updateNotificationBucket(key, {
                            teams: { userIds: v },
                          })
                        }
                        optionFilterProp="label"
                        disabled
                      />
                    </Form.Item>
                    <Form.Item label={t('template')} help={t('templateHelp')}>
                      <Input.TextArea
                        rows={6}
                        value={template}
                        onChange={(e) =>
                          updateNotificationBucket(key, {
                            template: e.target.value,
                          })
                        }
                      />
                    </Form.Item>
                  </Form>
                </Space>
              </Panel>
            );
          })}
        </Collapse>
      </Card>

      <Row justify="end">
        <Col>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={!isModified || isSaving}
            loading={isSaving}
            onClick={handleSave}
          >
            {t('save')}
          </Button>
        </Col>
      </Row>

      <Modal
        title={t('addProduct')}
        visible={isProductModalOpen}
        onCancel={() => setProductModalOpen(false)}
        onOk={confirmAddProduct}
      >
        <Form layout="vertical">
          <Form.Item label={t('productTitle')}>
            <Input
              value={draftProduct.productTitle}
              onChange={(e) =>
                setDraftProduct((d) => ({ ...d, productTitle: e.target.value }))
              }
            />
          </Form.Item>
          <Form.Item label={t('wcPid')}>
            <InputNumber
              style={{ width: '100%' }}
              value={draftProduct.woocommerceProductId as number | undefined}
              onChange={(v) =>
                setDraftProduct((d) => ({
                  ...d,
                  woocommerceProductId: v as number,
                }))
              }
            />
          </Form.Item>
          <Form.Item label={t('productClass')}>
            <Select
              value={draftProduct.productClass}
              onChange={(v: NightlyProductClass) =>
                setDraftProduct((d) => ({
                  ...d,
                  productClass: v,
                  productBloodType: PRODUCT_CLASSES_WITH_BLOOD_TYPE.includes(v)
                    ? d.productBloodType
                    : null,
                }))
              }
              options={[
                { value: 'PRBC', label: t('classPRBC') },
                { value: 'FFP', label: t('classFFP') },
                { value: 'Whole', label: t('classWhole') },
                { value: 'Accessory', label: t('classAccessory') },
              ]}
              placeholder={t('selectClass')}
            />
          </Form.Item>
          {draftProduct.productClass &&
            PRODUCT_CLASSES_WITH_BLOOD_TYPE.includes(
              draftProduct.productClass
            ) && (
              <Form.Item label={t('productBloodType')}>
                <Select
                  value={draftProduct.productBloodType ?? undefined}
                  onChange={(v: NightlyProductBloodType) =>
                    setDraftProduct((d) => ({ ...d, productBloodType: v }))
                  }
                  options={[
                    { value: 'Positive', label: t('bloodTypePositive') },
                    { value: 'Negative', label: t('bloodTypeNegative') },
                  ]}
                  placeholder={t('selectBloodType')}
                />
              </Form.Item>
            )}
        </Form>
      </Modal>
    </Space>
  );
};

export default NightlyInventoryCountSettings;
